import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { isNativeApp } from '../lib/native';
import { Scale, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };
const PATHS = { terms: '/terms', privacy: '/privacy-policy' };
const checked = new Map(); // userId -> true once verified this session

// Blocks the workspace until the signed-in user accepts the current Terms / Privacy version.
export const TermsAcceptanceGate = () => {
  const { user } = useAuth();
  const [required, setRequired] = useState([]);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.id || checked.get(user.id)) return;
    axios.get(`${API}/legal/acceptance-status`, { withCredentials: true })
      .then((r) => { setRequired(r.data.required || []); if (!(r.data.required || []).length) checked.set(user.id, true); })
      .catch(() => {});
  }, [user?.id]);

  if (!user || required.length === 0) return null;

  const accept = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/legal/accept`, { slugs: required.map((r) => r.slug) }, { withCredentials: true });
      setRequired(data.required || []);
      if (!(data.required || []).length) checked.set(user.id, true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not record your acceptance. Please try again.');
    } finally { setBusy(false); }
  };

  const isUpdate = required.some((r) => r.previously_accepted_version);
  const external = isNativeApp() ? undefined : '_blank';

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="terms-acceptance-gate">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-2xl p-6 sm:p-8" style={FONT}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#002FA7]/10 flex items-center justify-center"><Scale className="w-5 h-5 text-[#002FA7]" /></div>
          <h2 className="text-lg md:text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="terms-acceptance-title">
            {isUpdate ? 'We updated our legal terms' : 'Please review our legal terms'}
          </h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {isUpdate
            ? 'Before you continue, please review and accept the updated documents below. Your acceptance is recorded with the document version.'
            : 'To use CivilX TMS, please review and accept the documents below. Your acceptance is recorded with the document version.'}
        </p>
        <ul className="space-y-2 mb-5">
          {required.map((r) => (
            <li key={r.slug} className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-4 py-3" data-testid={`terms-acceptance-doc-${r.slug}`}>
              <div>
                <div className="font-semibold text-slate-900">{r.title}</div>
                <div className="text-xs text-slate-500">Version {r.version} · effective {r.effective_date}{r.previously_accepted_version ? ` · you accepted v${r.previously_accepted_version}` : ''}</div>
              </div>
              <a href={PATHS[r.slug]} target={external} rel="noreferrer" className="text-sm font-semibold text-[#002FA7] hover:underline shrink-0" data-testid={`terms-acceptance-read-${r.slug}`}>Read</a>
            </li>
          ))}
        </ul>
        <label className="flex items-start gap-3 text-sm text-slate-800 mb-4 cursor-pointer">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#002FA7]" data-testid="terms-acceptance-checkbox" />
          <span>I have read and agree to the {required.map((r) => r.title).join(' and the ')}.</span>
        </label>
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2 mb-3" data-testid="terms-acceptance-error">{error}</p>}
        <button type="button" onClick={accept} disabled={!agree || busy} data-testid="terms-acceptance-accept"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#00247D] disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Accept and continue
        </button>
      </div>
    </div>
  );
};

export default TermsAcceptanceGate;
